import { cloneStrategyRuleDSL } from "./strategy-builder.ts";
import type {
  CommunityReproduction,
  StrategyCommunityCapability,
} from "./strategy-community.ts";
import type { BacktestFormState } from "./types.ts";

type JsonRecord = Record<string, unknown>;

function record(value: unknown): JsonRecord | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as JsonRecord
    : null;
}

function nested(source: JsonRecord | null, key: string): JsonRecord | null {
  return record(source?.[key]);
}

function text(source: JsonRecord | null, key: string): string | undefined {
  const value = source?.[key];
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function bool(source: JsonRecord | null, key: string, fallback = false): boolean {
  return typeof source?.[key] === "boolean" ? source[key] as boolean : fallback;
}

const COMMUNITY_ENDPOINTS = {
  feed: "/api/v2/strategy-publications",
  detail: "/api/v2/strategy-publications/{publication_id}",
  publish: "/api/v2/strategy-publications",
  generatedPublish: "/api/v2/strategy-publications/generated",
  publishableSources: "/api/v2/strategy-publications/publishable-sources",
  favorites: "/api/v2/strategy-publications/favorites",
  favorite: "/api/v2/strategy-publications/{publication_id}/favorite",
  thanks: "/api/v2/strategy-publications/{publication_id}/thanks",
  report: "/api/v2/strategy-publications/{publication_id}/reports",
  duplicate: "/api/v2/strategy-publications/{publication_id}/duplicate-attributions",
  blockCreator: "/api/v2/strategy-publications/creators/{creator_id}/block",
  blockedCreators: "/api/v2/strategy-publications/blocked-creators",
} as const;

/** Fail-closed capability projection for old and partially deployed servers. */
export function resolveStrategyCommunityCapability(value: unknown): StrategyCommunityCapability {
  const envelope = record(value);
  const capabilities = nested(envelope, "capabilities") || envelope;
  const source = nested(capabilities, "strategy_community_v1");
  const schemaVersions = Array.isArray(source?.schema_versions)
    ? source.schema_versions.filter((item): item is number => Number.isInteger(item))
    : [];
  const liveExecution = bool(source, "live_execution");
  const creatorPayouts = bool(source, "creator_payouts");
  const performanceVerified = bool(source, "performance_verified");
  const publishingRequiresReview = bool(source, "publishing_requires_review");
  const serverAttestation = nested(source, "server_attestation");
  const enabled = source?.enabled === true
    && schemaVersions.includes(1)
    && text(source, "feed_url") === COMMUNITY_ENDPOINTS.feed
    && text(source, "detail_url_template") === COMMUNITY_ENDPOINTS.detail
    && source?.anonymous_feed === true
    && source?.publishing_requires_review === true
    && source?.live_execution === false
    && source?.creator_payouts === false
    && source?.performance_verified === false
    && source?.ranking_uses_raw_roi === false
    && text(source, "attestation") === "reproducibility_contract_v1"
    && text(serverAttestation, "type") === "reproducibility_contract_v1"
    && serverAttestation?.performance_verified === false
    && text(serverAttestation, "scope") === "recipe_structure_and_identity_only";
  return {
    enabled,
    publishing: enabled
      && text(source, "publish_url") === COMMUNITY_ENDPOINTS.publish
      && text(source, "publishable_sources_url") === COMMUNITY_ENDPOINTS.publishableSources,
    generatedPublishing: enabled
      && source?.generated_publishing === true
      && text(source, "generated_publish_url") === COMMUNITY_ENDPOINTS.generatedPublish
      && text(source, "publishable_sources_url") === COMMUNITY_ENDPOINTS.publishableSources,
    thanks: enabled && text(source, "thanks_url_template") === COMMUNITY_ENDPOINTS.thanks,
    favorites: enabled
      && text(source, "favorites_url") === COMMUNITY_ENDPOINTS.favorites
      && text(source, "favorite_url_template") === COMMUNITY_ENDPOINTS.favorite,
    reporting: enabled
      && source?.anonymous_reporting === true
      && text(source, "report_url_template") === COMMUNITY_ENDPOINTS.report,
    blocking: enabled
      && source?.creator_blocking === true
      && text(source, "block_creator_url_template") === COMMUNITY_ENDPOINTS.blockCreator
      && text(source, "blocked_creators_url") === COMMUNITY_ENDPOINTS.blockedCreators,
    duplicate: enabled
      && source?.anonymous_duplicate_attribution === true
      && text(source, "duplicate_attribution_url_template") === COMMUNITY_ENDPOINTS.duplicate,
    evidenceAttestation: enabled,
    liveExecution,
    creatorPayouts,
    performanceVerified,
    publishingRequiresReview,
  };
}

/** Imports one already-validated immutable recipe without retaining UI edits. */
export function applyCommunityReproduction(
  current: BacktestFormState,
  reproduction: CommunityReproduction,
): BacktestFormState {
  const assumptions = reproduction.assumptions;
  return {
    ...current,
    symbol: reproduction.symbol,
    provider: reproduction.provider,
    market: reproduction.market,
    timeframe: reproduction.timeframe,
    period: "Custom",
    strategyMode: "builder",
    strategyMethod: undefined, unsupportedMethodExecution: undefined,
    strategyName: reproduction.strategyLabel,
    prompt: "Visual Strategy Builder",
    strategyDSL: cloneStrategyRuleDSL(reproduction.strategyDSL),
    commissionRate: assumptions.commissionPercent,
    slippageBps: assumptions.slippageBps,
    positionSizePct: assumptions.positionSizePercent,
    executionModel: assumptions.executionModel,
    marginMode: assumptions.marginMode,
    leverage: assumptions.executionModel === "futures" ? assumptions.leverage : 1,
    maintenanceMarginPercent: assumptions.maintenanceMarginPercent,
    fundingRatePercent: assumptions.executionModel === "futures" ? assumptions.fundingRatePercent : 0,
    fundingIntervalHours: assumptions.fundingIntervalHours,
    liquidationFeePercent: assumptions.liquidationFeePercent,
    tradeDirection: assumptions.tradeDirection,
    exitMode: assumptions.exitMode,
    stopLossPct: assumptions.stopLossPercent,
    takeProfitPct: assumptions.takeProfitPercent,
    useAI: false,
    startDate: assumptions.startDate.slice(0, 10),
    endDate: assumptions.endDate.slice(0, 10),
  };
}
